import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Avatar,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Sms as SmsIcon,
  Dashboard as DashboardIcon,
  AdminPanelSettings as AdminIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

export default function AdminPanel({ token }) {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ totalUsers: 0, totalCalls: 0, totalSms: 0, totalIvr: 0 });
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", role: "user", modules: "ivr" });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [showPassword, setShowPassword] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" });

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/users`, { headers: getHeaders() });
      if (!res.ok) throw new Error("Failed to fetch");
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to load users", severity: "error" });
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/stats`, { headers: getHeaders() });
      if (res.ok) setStats(await res.json());
    } catch {}
  }, [token]);

  useEffect(() => { fetchUsers(); fetchStats(); }, [fetchUsers, fetchStats]);

  const handleSave = async () => {
    try {
      const url = editUser ? `${API}/admin/users/${editUser.id}` : `${API}/admin/users`;
      const method = editUser ? "PUT" : "POST";
      const body = editUser
        ? { email: form.email, role: form.role, modules: form.modules, ...(form.password ? { password: form.password } : {}) }
        : form;

      const res = await fetch(url, { method, headers: getHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");

      setSnackbar({ open: true, message: editUser ? "User updated" : "User created", severity: "success" });
      setDialogOpen(false);
      setEditUser(null);
      setForm({ email: "", password: "", role: "user" });
      fetchUsers();
      fetchStats();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API}/admin/users/${id}`, { method: "DELETE", headers: getHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setSnackbar({ open: true, message: "User deleted", severity: "success" });
      setDeleteConfirm(null);
      fetchUsers();
      fetchStats();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    }
  };

  const openEdit = (user) => {
    setEditUser(user);
    setForm({ email: user.email, password: "", role: user.role || "user", modules: user.modules || "ivr" });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditUser(null);
    setForm({ email: "", password: "", role: "user", modules: "ivr" });
    setDialogOpen(true);
  };

  const filtered = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const getInitials = (email) => email ? email.split("@")[0].substring(0, 2).toUpperCase() : "U";

  const statCards = [
    { label: "Total Users", value: stats.totalUsers, icon: PeopleIcon, color: "#6366f1" },
    { label: "Total Calls", value: stats.totalCalls, icon: PhoneIcon, color: "#10b981" },
    { label: "Total SMS", value: stats.totalSms, icon: SmsIcon, color: "#f59e0b" },
    { label: "IVR Records", value: stats.totalIvr, icon: DashboardIcon, color: "#3b82f6" },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "14px", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <AdminIcon sx={{ color: "#fff", fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ color: "#0f172a", fontWeight: 800 }}>Admin Panel</Typography>
            <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>Manage users and monitor platform activity</Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: 2, mb: 4 }}>
        {statCards.map((s) => (
          <Card key={s.label} sx={{ background: "#fff" }}>
            <CardContent sx={{ p: "20px !important" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Box>
                  <Typography sx={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 600, mb: 0.5 }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: "1.8rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{s.value}</Typography>
                </Box>
                <Box sx={{ width: 42, height: 42, borderRadius: "12px", background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <s.icon sx={{ color: s.color, fontSize: 22 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Users Table */}
      <Card>
        <CardContent sx={{ p: "24px !important" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Users</Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                placeholder="Search users..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>,
                }}
                sx={{ width: 260 }}
              />
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ background: "#6366f1", "&:hover": { background: "#4f46e5" } }}>
                Add User
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, color: "#64748b", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", background: "#f8fafc" } }}>
                  <TableCell>User</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Modules</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: "#94a3b8" }}>
                      <PeopleIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                      <Typography>No users found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((user) => (
                    <TableRow key={user.id} sx={{ "&:hover": { background: "#f8fafc" } }}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar sx={{ width: 36, height: 36, fontSize: "0.8rem", fontWeight: 700, background: user.role === "admin" ? "linear-gradient(135deg, #6366f1, #8b5cf6)" : "linear-gradient(135deg, #94a3b8, #64748b)" }}>
                            {getInitials(user.email)}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>{user.email.split("@")[0]}</Typography>
                            <Typography sx={{ color: "#94a3b8", fontSize: "0.8rem" }}>{user.email}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role === "admin" ? "Admin" : "User"}
                          size="small"
                          sx={{
                            background: user.role === "admin" ? "#6366f115" : "#64748b15",
                            color: user.role === "admin" ? "#6366f1" : "#64748b",
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {(user.modules || "ivr").split(",").map((m) => (
                            <Chip
                              key={m}
                              label={m === "ivr" ? "IVR" : "AI Voice"}
                              size="small"
                              sx={{
                                background: m === "ivr" ? "#eff6ff" : "#f5f3ff",
                                color: m === "ivr" ? "#3b82f6" : "#8b5cf6",
                                fontWeight: 600, fontSize: "0.7rem",
                              }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: "0.85rem", color: "#64748b" }}>
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(user)} sx={{ color: "#6366f1" }}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => setDeleteConfirm(user)} sx={{ color: "#ef4444", ml: 0.5 }}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, pb: 1 }}>
          {editUser ? "Edit User" : "Create User"}
          <IconButton onClick={() => setDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "16px !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TextField
              label="Email"
              fullWidth
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              type="email"
              autoComplete="off"
            />
            <TextField
              label={editUser ? "New Password (leave empty to keep)" : "Password"}
              fullWidth
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={form.role}
                label="Role"
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Module Access</InputLabel>
              <Select
                value={form.modules || "ivr"}
                label="Module Access"
                onChange={(e) => setForm({ ...form, modules: e.target.value })}
              >
                <MenuItem value="ivr">IVR Only</MenuItem>
                <MenuItem value="ai_voice">AI Voice Only</MenuItem>
                <MenuItem value="ivr,ai_voice">Both (IVR + AI Voice)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: "#64748b" }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.email || (!editUser && !form.password)} sx={{ background: "#6366f1", "&:hover": { background: "#4f46e5" } }}>
            {editUser ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete User</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>This action cannot be undone.</Alert>
          <Typography>Are you sure you want to delete <strong>{deleteConfirm?.email}</strong>?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ color: "#64748b" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(deleteConfirm.id)}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
